import React, { useCallback, useRef, useState } from "react"
import styled from "styled-components"
import PropTypes from "prop-types"

const ImageContainer = styled.div`
  position: relative;
  display: flex !important;
  justify-content: center;
  border-radius: ${({ radius }) => radius && radius + "rem"};

  width: ${({ width }) => (width ? width + "px" : "100%")};
  height: ${({ height }) => (height ? height + "px" : "auto")};
  max-height: 100%;
`

const MainImage = styled.img`
  border: none;
  z-index: 10;
  object-fit: ${({ objectFit }) => objectFit ?? "cover"};
  object-position: ${({ objectPosition }) => objectPosition};
  width: 100%;
  margin: 0;
  max-height: 100%;
  height: auto;
  max-width: 100%;
`

const StyledBorderRadius = styled.div`
  overflow: ${({ showOverflow }) => (showOverflow ? "visible" : "hidden")};
  aspect-ratio: ${({ aspectRatio }) => aspectRatio};
  display: flex;

  border-radius: ${({ radius }) => radius && radius + "rem"};
  max-height: 100%;
  max-width: 100%;
`

function getRefRatio(ref) {
  return `${ref?.current?.naturalWidth}/${ref?.current?.naturalHeight}`
}

const Image = ({
  file,
  eager,
  src,
  radius,
  className,
  width,
  height,
  style,
  borderStyle,
  noDimensions,
  aspectRatio,
  ...props
}) => {
  if (src) return <img src={src} style={style} {...props} />
  const { cdn } = file ?? {}
  if (!cdn) return null
  const [loaded, setLoaded] = useState(false)
  const mainImageRef = useRef(null)

  return (
    <ImageContainer
      className={`klipfolio-image-wrapper ${className ? className : ""}`}
      width={width}
      height={height}
      style={style}
      radius={radius}
      noDimensions={noDimensions}
    >
      {radius ? (
        <RadiusImage
          radius={radius}
          borderStyle={borderStyle}
          eager={eager}
          cdn={cdn}
          loaded={loaded}
          setLoaded={setLoaded}
          {...props}
          mainImageRef={mainImageRef}
          aspectRatio={
            aspectRatio
              ? aspectRatio
              : radius && loaded ? getRefRatio(mainImageRef) : undefined
          }
        />
      ) : (
        <StandardImage eager={eager} cdn={cdn} {...props} />
      )}
    </ImageContainer>
  )
}

Image.propTypes = {
  file: PropTypes.object,
  src: PropTypes.string,
  eager: PropTypes.bool,
  radius: PropTypes.number,
  className: PropTypes.string,
  width: PropTypes.number,
  height: PropTypes.number,
  alt: PropTypes.string.isRequired,
  style: PropTypes.object,
  borderStyle: PropTypes.object,
  noDimensions: PropTypes.bool,
  aspectRatio: PropTypes.string,
}

const StandardImage = ({ eager, cdn, ...props }) => {
  return (
    <MainImage
      src={cdn}
      width={props.imageHeight}
      height={props.imageWidth}
      {...props}
      loading={eager ? "eager" : "lazy"}
    />
  )
}

StandardImage.propTypes = {
  radius: PropTypes.number,
  borderStyle: PropTypes.object,
  mainImageRef: PropTypes.any,
  eager: PropTypes.bool,
  cdn: PropTypes.string,
  imageWidth: PropTypes.number,
  imageHeight: PropTypes.number,
}

const RadiusImage = ({
  radius,
  borderStyle,
  eager,
  cdn,
  mainImageRef,
  setLoaded,
  aspectRatio,
  onLoad,
  ...props
}) => {

  const handleLoad = useCallback((e) => {
    if(onLoad) {
      onLoad(e)
    }
    if(setLoaded) {
      setLoaded(true)
    }
  }, [onLoad, setLoaded])
  return (
    <StyledBorderRadius
      radius={radius}
      style={borderStyle}
      aspectRatio={aspectRatio}
    >
      <MainImage
        ref={mainImageRef}
        src={cdn}
        width={props.imageWidth}
        height={props.imageHeight}
        {...props}
        onLoad={handleLoad}
        loading={eager ? "eager" : "lazy"}
      />
    </StyledBorderRadius>
  )
}

RadiusImage.propTypes = {
  radius: PropTypes.number,
  borderStyle: PropTypes.object,
  mainImageRef: PropTypes.any,
  eager: PropTypes.bool,
  cdn: PropTypes.string,
  placeholder: PropTypes.string,
  loaded: PropTypes.bool,
  setLoaded: PropTypes.func,
  aspectRatio: PropTypes.string,
  imageWidth: PropTypes.number,
  imageHeight: PropTypes.number,
  onLoad: PropTypes.func
}
Image.defaultProps = {
  alt: "klipfolio image",
  full: true,
}

export default Image
